/* The return record itself — `/sales/returns/:id`'s content, and later a drawer over the list
   reusing the same component (mirrors `orders/OrderDetail.js`). Takes finished data (`item`,
   already-loaded) and a `lang`; knows nothing about `:id`, the request, or the loading/error
   states — that is `Page.js`'s job.

   Read-only, same as orders: 1C/Excalibur is the source of truth, no dirty state, no save bar.

   Field shapes come from the ACTUAL `excalibur/returns/{id}/` response, not the written spec:
   there is no money anywhere (no `total`, no per-item price), no `reason` on an item, no
   `tracked_geolocation` (that field exists only on `Order`, so this page has no geolocation card
   at all — not even an empty one), no `related_order`, and no `department`. None of those are
   rendered here. `items_count`/`units_count` are on the LIST serializer only — the head's tile
   derives both from the loaded `item.items` instead of reading fields that are not in this response.

   Layout, as the order's (`orders/OrderDetail.js`): the head (number, status, external id) and
   the tiles (lines / units, the return's date and time, the physical return date); then the items
   and the comment, and short titled groups — "Деталі", "Контрагент" (`ReturnGroups`, `DetailGroup`
   cards). On the page the groups are `Page.js`'s rail; in the modals one column under the items.
   Each value is on the screen once, and none of them is lost. There is no group for the physical
   return: its only field, the date, is a tile. */

import React from "react";
import { Table, Tag, Typography } from "antd";
import { LinkOutlined, RollbackOutlined, SyncOutlined } from "@ant-design/icons";

import { DetailGroup, KpiStrip } from "../../component/detail-page";
import { PersonCell, dash, textOf } from "../../component/table-cells";
import { RETURNS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";

import { STATUS_ICON } from "./parts";

/** items/units, once — the header's KPI strip is the reader (the items table has no footer: it
    would only repeat the tile). */
const itemTotals = item => ({
  itemsCount: item.items?.length || 0,
  unitsCount: (item.items || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
});

/** The page head — entity icon, number, status, the 1C reference, and the KPI strip. Its own
    export because `Page.js` needs it OUTSIDE `dp-grid` (spec/03 archetype B), and because the
    list's record modal reuses it verbatim as the antd `Modal` `title` — one header for the record,
    not a page version and a separate, thinner modal version that drift apart. Icon is
    `RollbackOutlined`, the same glyph `AppNav.js` uses for the "Повернення" nav row, so the record
    reads as the same kind of thing whichever nav item led here. */
export function ReturnDetailHead({ item, lang }) {
  const t = key => translate(RETURNS_PAGE, key, lang);
  const { Icon, tone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(RETURNS_PAGE, `return_status_${item.status}`, lang) || item.status;
  const { itemsCount, unitsCount } = itemTotals(item);

  return (
    <div className="dp-head dp-head--fluid">
      <div className="dp-head__body">
        <div className="dp-head__top">
          <div className={`dp-head__icon dp-head__icon--${tone}`}>
            <RollbackOutlined />
          </div>
          <div className="dp-head__main">
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{item.name ? textOf(item.name) : `#${item.id}`}</h1>
              {/* The filled pill `visit/parts.js`'s `StatusTag` already established — a `-bg`
                 tint plus a `-700` label plus an icon, never colour alone (spec/01#status-colour).
                 Built inline rather than imported: that component reads its labels from
                 `VISIT_PAGE`. */}
              <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone}`}>
                {statusLabel}
              </Tag>
              {/* Bordered, not filled — this is the record's id in the external system that feeds
                 ours, the same "foreign system data reads as a plain fact, never one of our
                 verdicts" split `OrderStatusTag` draws elsewhere in this app. The label names no
                 system: which one it is belongs to the deployment, not to the field. */}
              {item.external_id ? (
                <Tag icon={<SyncOutlined />}>
                  {t("k_external_id")}: {textOf(item.external_id)}
                </Tag>
              ) : null}
            </div>
          </div>
        </div>
        {/* Always the same THREE tiles, an empty value shown as `dash` rather than one tile fewer.
            A strip whose tile count depends on the record reads as a rendering fault, and with
            `flex: 1 1 0` each surviving tile also takes the missing one's share — two tiles on a
            wide page stretched ~800px each, which is how the numbers got lost in the first place.
            `date_of_physical_return` is empty on 16 of 30 returns on dev and across every status
            (10 of 16 `new` have it, 2 of 3 `closed` do NOT), so it is genuinely "not recorded",
            not "not yet returned" — there is no honest substitute figure to put here, and
            `dash` is `--neutral-300`, quiet enough not to read as a zero. */}
        <KpiStrip
          items={[
            { key: "positions", label: translate(RETURNS_PAGE, "k_positions_units", lang), value: `${itemsCount} / ${unitsCount}` },
            {
              key: "return_date",
              label: translate(RETURNS_PAGE, "k_return_date", lang),
              /* With its time: this tile is the one place the return's date and time are shown. */
              value: item.datetime ? dayjs(item.datetime).format("DD.MM.YYYY HH:mm") : dash,
            },
            {
              key: "physical_date",
              label: translate(RETURNS_PAGE, "k_physical_return_date", lang),
              value: item.date_of_physical_return ? dayjs(item.date_of_physical_return).format("DD.MM.YYYY") : dash,
            },
          ]}
        />
      </div>
    </div>
  );
}

/**
 * Everything about the return that is not its items: "Деталі" (with the point), then
 * "Контрагент". Each value is on the
 * screen once — the head carries the number, status and external id, the tiles the dates — and a
 * field is shown only when it holds something, the visit ("—": taken outside a visit) excepted.
 *
 * `place`: "page" — the rail, where `Page.js` puts the counterparty card (`CounterpartySection`)
 * after these, so only "Деталі" is drawn here; "modal" — one column under the items, with its own
 * "Контрагент". `hideCounterparty`: the counterparty card's own modal, where that group is left out.
 */
export function ReturnGroups({ item, lang, place = "modal", hideCounterparty = false }) {
  const t = key => translate(RETURNS_PAGE, key, lang);
  const at = value => (value ? <span className="dp-nowrap">{dayjs(value).format("DD.MM.YYYY HH:mm")}</span> : null);
  return (
    <>
      <DetailGroup
        title={t("sec_details")}
        rows={[
          item.created_by ? { key: "created_by", label: t("k_created_by"), value: <PersonCell user={item.created_by} strong={false} /> } : null,
          {
            key: "visit",
            label: t("k_visit"),
            value: item.visit ? (
              <Typography.Link href={`/visit/${item.visit}`} target="_blank" rel="noopener noreferrer">
                {t("k_visit")} <LinkOutlined />
              </Typography.Link>
            ) : (
              dash
            ),
          },
          item.point?.name ? { key: "point", label: t("k_point"), value: pointText(item.point) } : null,
          item.supervisor ? { key: "supervisor", label: t("k_supervisor"), value: <PersonCell user={item.supervisor} strong={false} /> } : null,
          item.status_changed ? { key: "status_changed", label: t("k_status_changed"), value: at(item.status_changed) } : null,
          item.created ? { key: "created", label: t("k_created"), value: at(item.created) } : null,
          item.modified ? { key: "modified", label: t("k_modified"), value: at(item.modified) } : null,
        ]}
      />
      {/* "Контрагент" — after "Деталі"; the name row is "Назва", as on the page's counterparty card,
          since the title already says whose it is. In the counterparty card's own modal it has
          nothing left to say and is not drawn. */}
      {place === "modal" && !hideCounterparty && item.counterparty ? (
        <DetailGroup
          title={t("k_counterparty")}
          rows={[
            { key: "name", label: t("k_name"), value: textOf(item.counterparty.name) },
            item.counterparty.address ? { key: "address", label: t("k_address"), value: textOf(item.counterparty.address) } : null,
          ]}
        />
      ) : null}
    </>
  );
}

/** A point as one line: its name, then its address. */
export const pointText = point => [point?.name, point?.address].filter(Boolean).join(" · ");

/* `layout`: "modal" (default) puts `ReturnGroups` under the items; "page" leaves them to
   `Page.js`'s rail. `hideCounterparty` is for the counterparty card's Returns tab, whose modal is
   open over that very counterparty. */
const ReturnDetail = ({ item, lang, showHeader = true, hideCounterparty = false, layout = "modal" }) => {
  const t = key => translate(RETURNS_PAGE, key, lang);

  const itemColumns = [
    /* The line's position in the return as the API sent it — derived from the render index, not
       stored: nothing on `ReturnItem` records a line number. `pagination={false}`, so it is 1..N. */
    { key: "line", title: t("k_line_no"), width: 48, align: "right", render: (value, row, index) => <span className="lp-num">{index + 1}</span> },
    {
      key: "name",
      title: t("k_product_name"),
      ellipsis: true,
      render: (value, row) => textOf(row.product?.name),
    },
    { key: "code", title: t("k_product_code"), width: 112, render: (value, row) => textOf(row.product?.code) },
    {
      key: "quantity",
      title: t("k_quantity"),
      width: 110,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.quantity)}</span>,
    },
  ];

  return (
    <>
      {showHeader ? <ReturnDetailHead item={item} lang={lang} /> : null}

      {/* No summary footer: the lines and units it would add up are the head's "Позицій / одиниць"
         tile. */}
      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_items")}
        </Typography.Text>
        <Table
          rowKey="id"
          size="middle"
          columns={itemColumns}
          dataSource={item.items || []}
          pagination={false}
          scroll={{ x: 400 }}
          locale={{ emptyText: t("no_items") }}
        />
      </div>
      {/* The comment carries the reason for the return ("пошкоджена упаковка"), which reads as
         content, not metadata — its own card under the items. `additional_info` is not shown: it
         is `{}` on most records and never more than duplicate metadata where it is not. */}
      {item.comments ? (
        <div className="dp-card">
          <Typography.Text strong className="dp-sec-title">
            {t("k_comment")}
          </Typography.Text>
          <div className="dp-note__box">{textOf(item.comments)}</div>
        </div>
      ) : null}
      {layout === "modal" ? <ReturnGroups item={item} lang={lang} place="modal" hideCounterparty={hideCounterparty} /> : null}
    </>
  );
};

export default ReturnDetail;
